import { Conversation, ConversationsData, Message, User } from '@nextforce/api'
import { UseMutationResult } from '@tanstack/react-query'
import { ConversationsSection } from './queries/context'

export function getConversationUnread(
  conversation: Conversation,
  user: User | undefined
): boolean {
  const status = conversation.status

  if (!status || !status.recentMessage || !user?.id) {
    return false
  }

  if (status.recentMessage.user.id === user.id) {
    return false
  }

  return (status.lastViewedTimestamp ?? 0) < status.recentMessage.createdAt
}

export function defaultCustomSection(sections: ConversationsSection[]) {
  const customGroupsSection = sections.find(
    section => section.type === 'custom'
  )
  const storeGroupsSection = sections.find(section => section.type === 'group')
  if (customGroupsSection && storeGroupsSection) {
    const defaultGroupsSection =
      customGroupsSection.unread >= storeGroupsSection.unread
    return defaultGroupsSection
  } else {
    return undefined
  }
}

export async function sendDirectMessage(
  navigation: any,
  mutation: UseMutationResult<Conversation, unknown, string>,
  recipientId: string
) {
  const directConversation = await mutation.mutateAsync(recipientId)

  navigation.push('messages', {
    id: directConversation.id,
  })
}

export function convertMessagesToConversations(
  messages?: Message[],
  conversations?: ConversationsData | Conversation[]
): Conversation[] {
  if (!messages || !conversations) return []
  return messages
    .flatMap(message => {
      const conversation = conversations.find(
        c => c.id === message.conversationId
      )
      if (!conversation) return []
      return {
        ...conversation,
        status: {
          recentMessage: message,
          lastViewedTimestamp: conversation?.status?.lastViewedTimestamp,
        },
      }
    })
    .sort(
      (a, b) =>
        (b.status.recentMessage.createdAt ?? 0) -
        (a.status.recentMessage.createdAt ?? 0)
    )
}

export function isRepository(conversation: Conversation | undefined) {
  if (!conversation) return false
  return (
    conversation.type === 'repository' ||
    conversation.type === 'companyRepository' ||
    conversation.type === 'storeRepository'
  )
}

export const sortConversationsByMostRecentMessage = (
  a: Conversation,
  b: Conversation
) =>
  (b.status?.recentMessage?.createdAt ?? 0) -
  (a.status?.recentMessage?.createdAt ?? 0)

export function prioritizeConversations(
  conversations: Conversation[],
  user?: User,
  urgentUnreadConversationIds?: string[]
): Conversation[] {
  return conversations.sort((a, b) => {
    // Check if conversations are unread
    const aUnread = getConversationUnread(a, user)
    const bUnread = getConversationUnread(b, user)

    // Check if conversations are urgent AND unread
    const aUrgentUnread = urgentUnreadConversationIds?.includes(a.id)
    const bUrgentUnread = urgentUnreadConversationIds?.includes(b.id)

    // Check if conversations are favorites
    const aFavorite = a.favorite
    const bFavorite = b.favorite

    // Priority calculation (higher = more important):
    // 1. Urgent unread messages: 4 points
    // 2. Favorite unread messages: 3 points
    // 3. Favorite read messages: 2 points
    // 4. Other unread messages: 1 point
    // 5. Other read messages: 0 points
    let aPriority = 0
    let bPriority = 0

    if (aUrgentUnread) aPriority = 4
    else if (aFavorite && aUnread) aPriority = 3
    else if (aFavorite) aPriority = 2
    else if (aUnread) aPriority = 1

    if (bUrgentUnread) bPriority = 4
    else if (bFavorite && bUnread) bPriority = 3
    else if (bFavorite) bPriority = 2
    else if (bUnread) bPriority = 1

    // Sort by priority first (descending)
    if (aPriority !== bPriority) {
      return bPriority - aPriority
    }

    // If same priority, sort by most recent message (descending)
    return sortConversationsByMostRecentMessage(a, b)
  })
}

export function hasUrgentUnreadMessages(
  conversation: Conversation,
  messages?: Message[]
) {
  const lastViewedTimestamp = conversation.status?.lastViewedTimestamp ?? 0
  return messages?.some(
    m =>
      m.conversationId === conversation.id &&
      m.urgent &&
      m.createdAt > lastViewedTimestamp
  )
}